/**
 * PDP sticky buy bar — shows when the main Add to cart / pre-order CTA is off-screen
 * (mobile + desktop). Hides while the primary purchase button is visible.
 */
(function () {
  const BAR_SELECTOR = '[data-pdp-sticky-buy-bar]';
  const CONFIG_SELECTOR = '[data-pdp-sticky-buy-bar-config]';
  const VARIANT_EVENT = 'variant:update';

  /** @param {number} cents */
  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat || Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Parse a shop-formatted price string ("$43.20 NZD") into cents.
   * @param {string} str
   * @returns {number | null}
   */
  function parsePriceToCents(str) {
    if (!str) return null;
    const cleaned = String(str).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    const num = parseFloat(cleaned);
    if (!Number.isFinite(num)) return null;
    return Math.round(num * 100);
  }

  /**
   * @param {number} lineTotalCents
   * @param {number} packQuantity
   * @param {{ unitsPerBox?: number, container?: string, containerPlural?: string }} packMeta
   * @returns {string}
   */
  /**
   * @param {number} packQuantity
   * @param {number} lineTotalCents
   * @param {string} moneyFormat
   * @returns {string}
   */
  function formatLimitedQtyLine(packQuantity, lineTotalCents, moneyFormat) {
    const packs = Math.max(1, packQuantity || 1);
    const boxLabel = packs === 1 ? 'box' : 'boxes';
    return packs + ' ' + boxLabel + ' · ' + formatMoney(lineTotalCents, moneyFormat);
  }

  function formatPackMetaLine(lineTotalCents, packQuantity, packMeta, moneyFormat) {
    const unitsPerBox = packMeta?.unitsPerBox;
    if (!unitsPerBox || unitsPerBox < 1) return '';

    const packs = Math.max(1, packQuantity || 1);
    const totalUnits = unitsPerBox * packs;
    const perUnitCents = Math.round(lineTotalCents / totalUnits);
    const container = packMeta.container || 'can';
    const containerPlural = packMeta.containerPlural || container + 's';

    return (
      formatMoney(perUnitCents, moneyFormat) +
      ' per ' +
      container +
      ' · ' +
      unitsPerBox +
      ' ' +
      containerPlural +
      ' per box'
    );
  }

  /**
   * @param {HTMLElement | null} metaEl
   * @param {number} lineTotalCents
   * @param {number} packQuantity
   * @param {{ unitsPerBox?: number, container?: string, containerPlural?: string }} packMeta
   */
  function updatePackMeta(metaEl, lineTotalCents, packQuantity, packMeta, moneyFormat, isLimitedEdition) {
    if (!(metaEl instanceof HTMLElement)) return;

    const text = isLimitedEdition
      ? formatLimitedQtyLine(packQuantity, lineTotalCents, moneyFormat)
      : formatPackMetaLine(lineTotalCents, packQuantity, packMeta, moneyFormat);

    if (!text) return;
    if (!isLimitedEdition && !packMeta?.unitsPerBox) return;
    if (!text) {
      metaEl.hidden = true;
      return;
    }

    metaEl.textContent = text;
    metaEl.hidden = false;
  }

  /** @returns {number} */
  function getBccPackQuantity() {
    const qtyInput = document.querySelector('[data-bcc-tier-picker] [data-bcc-qty-input]');
    if (!(qtyInput instanceof HTMLInputElement)) return 1;
    const qty = parseInt(qtyInput.value, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * @param {number} productId
   * @returns {HTMLInputElement | null}
   */
  function findQuantityInput(productId) {
    const selectors = [
      'product-form-component[data-product-id="' + productId + '"] input[name="quantity"]',
      '.product-information input[name="quantity"]',
      'form[action*="/cart/add"] input[name="quantity"]',
      'input[name="quantity"]',
    ];
    for (const selector of selectors) {
      const input = document.querySelector(selector);
      if (input instanceof HTMLInputElement) return input;
    }
    return null;
  }

  /**
   * Cents taken off the line by the Admin volume discount, mirroring the Fridge Fill
   * ladder. Selling plans price through their own discount, so it is one-time only.
   * @param {number} productId
   * @param {number} qty
   * @param {boolean} isSubscription
   * @returns {number}
   */
  function getVolumeDiscountCents(productId, qty, isSubscription) {
    if (isSubscription) return 0;
    const ff =
      document.querySelector('[data-pdp-ff][data-product-id="' + productId + '"]') ||
      document.querySelector('[data-pdp-ff]');
    if (!ff || ff.getAttribute('data-volume-discount-enabled') !== 'true') return 0;

    const cents = parseInt(ff.getAttribute('data-volume-discount-cents') || '0', 10) || 0;
    const minBoxes = parseInt(ff.getAttribute('data-volume-min-boxes') || '0', 10) || 0;
    if (!cents || !minBoxes || qty < minBoxes) return 0;
    return cents;
  }

  /**
   * Box count from the Fridge Fill ladder or the product form quantity input.
   * @param {number} productId
   * @returns {number}
   */
  function getPackQuantity(productId) {
    if (document.querySelector('[data-bcc-tier-picker]')) {
      return getBccPackQuantity();
    }

    const qtyInput = findQuantityInput(productId);
    if (qtyInput) {
      const fromInput = parseInt(qtyInput.value, 10);
      if (Number.isFinite(fromInput) && fromInput > 0) return fromInput;
    }

    const ff = document.querySelector('[data-pdp-ff][data-product-id="' + productId + '"]') ||
      document.querySelector('[data-pdp-ff]');
    if (ff) {
      const active = ff.querySelector('[data-pdp-ff-qty].is-active');
      const fromFf = active ? parseInt(active.getAttribute('data-pdp-ff-qty') || '', 10) : NaN;
      if (Number.isFinite(fromFf) && fromFf > 0) return fromFf;
    }

    return 1;
  }

  /**
   * Unit prices for the selected variant / subscription option.
   * @param {{ variant: { price: number, compare_at_price?: number } }} config
   */
  function getActiveUnitPrices(config) {
    const fieldset = document.querySelector('.shopify_subscriptions_fieldset');
    const checked = fieldset?.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) {
      let priceCents = parsePriceToCents(checked.dataset.variantPrice);
      if (priceCents != null) {
        const compareCents =
          parsePriceToCents(checked.dataset.variantCompareAtPrice) ??
          config.variant.compare_at_price ??
          0;
        const isSubscription = checked.dataset.radioType === 'selling_plan';
        if (isSubscription && compareCents > 0) {
          try {
            const node = document.getElementById('sk-first-sub-discount-config');
            const cfg = node ? JSON.parse(node.textContent || '{}') : null;
            if (cfg && cfg.enabled && cfg.isFirstSubscription !== false) {
              priceCents = Math.round(compareCents * 0.8);
            }
          } catch (_) {
            /* keep selling-plan price */
          }
        }
        return {
          priceCents,
          compareCents,
          isSubscription,
          checked,
          hasSubscriptionWidget: true,
        };
      }
    }

    return {
      priceCents: config.variant.price,
      compareCents: config.variant.compare_at_price ?? 0,
      isSubscription: false,
      checked: null,
      hasSubscriptionWidget: Boolean(fieldset),
    };
  }

  function getNavHeight() {
    const header = document.getElementById('header-component');
    if (header instanceof HTMLElement) {
      const height = header.offsetHeight;
      if (height > 0) return height;
      const bottom = header.getBoundingClientRect().bottom;
      if (bottom > 0) return bottom;
    }

    const parsed = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--pdp-nav-height')
    );
    if (Number.isFinite(parsed) && parsed > 0) return parsed;

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    return Number.isFinite(headerHeight) && headerHeight > 0 ? headerHeight : 60;
  }

  /**
   * Offset for intersection checks.
   * Important: must NOT include the sticky bar height itself, otherwise we can
   * create a feedback loop where showing the bar increases the offset and
   * makes the primary CTA appear "hidden", keeping the bar visible at the top
   * of the page (observed on BCC PDP).
   */
  function getHeaderOffset() {
    const cssTop = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--pdp-sticky-bar-top')
    );
    if (Number.isFinite(cssTop) && cssTop >= 0) return cssTop;

    const header = document.getElementById('header-component');
    const headerSection = header?.closest?.('.header-section');
    const anchor =
      headerSection instanceof HTMLElement ? headerSection : document.getElementById('header-group');
    if (anchor instanceof HTMLElement) {
      const bottom = anchor.getBoundingClientRect().bottom;
      if (bottom > 0) return bottom;
    }

    return getNavHeight();
  }

  /** @param {boolean} configIsBcc */
  function isBccPage(configIsBcc) {
    return configIsBcc || Boolean(document.querySelector('[data-bcc-tier-picker]'));
  }

  /**
   * Primary purchase CTA (standard ATC, BCC pre-order, or first cart/add control on the PDP).
   * @param {number} productId
   * @param {boolean} isBcc
   * @returns {HTMLElement | null}
   */
  function findMainPurchaseCta(productId, isBcc) {
    if (isBcc) {
      const bccCta = document.querySelector('[data-bcc-tier-picker] .bcc-tier-picker__cta');
      if (bccCta instanceof HTMLElement) return bccCta;
    }

    const formSelectors = [
      '.product-information product-form-component[data-product-id="' + productId + '"]',
      'product-form-component[data-product-id="' + productId + '"]',
      '.product-information product-form-component',
      'product-form-component',
    ];

    for (const selector of formSelectors) {
      const formComponent = document.querySelector(selector);
      if (!formComponent) continue;

      const button =
        formComponent.querySelector('[ref="addToCartButton"]') ||
        formComponent.querySelector('button[name="add"]') ||
        formComponent.querySelector('.add-to-cart-button button') ||
        formComponent.querySelector('.add-to-cart-button');

      if (button instanceof HTMLElement) return button;
    }

    const scopedAdd = document.querySelector(
      '.product-information button[name="add"]:not([data-pdp-sticky-cta])'
    );
    if (scopedAdd instanceof HTMLElement) return scopedAdd;

    const mainAdd = document.querySelector('button[name="add"]:not([data-pdp-sticky-cta])');
    return mainAdd instanceof HTMLElement ? mainAdd : null;
  }

  /**
   * @param {number} productId
   * @returns {HTMLFormElement | null}
   */
  function findAddToCartForm(productId) {
    const selectors = [
      '.product-information product-form-component[data-product-id="' + productId + '"] form[action*="/cart/add"]',
      'product-form-component[data-product-id="' + productId + '"] form[action*="/cart/add"]',
      '.product-information form[action*="/cart/add"]',
      'form.shopify-product-form[action*="/cart/add"]',
      'form[action*="/cart/add"]',
    ];

    for (const selector of selectors) {
      const form = document.querySelector(selector);
      if (form instanceof HTMLFormElement) return form;
    }

    return null;
  }

  function isCartDrawerOpen() {
    return !!document.querySelector('.cart-drawer__dialog[open]');
  }

  /**
   * @param {HTMLElement} bar
   * @param {boolean} visible
   */
  function setBarVisible(bar, visible) {
    if (visible && isCartDrawerOpen()) visible = false;
    bar.classList.toggle('is-visible', visible);
    bar.toggleAttribute('hidden', !visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    /* Keep hidden sticky CTAs out of the tab order (WCAG 2.4.3). */
    if (visible) {
      bar.removeAttribute('inert');
    } else {
      bar.setAttribute('inert', '');
    }
    document.body.classList.toggle('has-pdp-sticky-bar-visible', visible);
    requestAnimationFrame(() => {
      syncPdpHeaderStackMetrics();
      syncStickyBarTopOffset();
    });
  }

  /**
   * Keep the bar visually "attached" to the header stack while scrolling.
   * The header group contains announcement rows that can scroll away; using
   * its live bounding box avoids gaps/overlaps.
   */
  function syncStickyBarTopOffset() {
    const header = document.getElementById('header-component');
    const headerSection = header?.closest?.('.header-section');
    const anchor =
      headerSection instanceof HTMLElement
        ? headerSection
        : document.getElementById('header-group');
    if (!(anchor instanceof HTMLElement)) return;

    // Use layout height (not scroll position) so we don't need a scroll handler.
    // Header is sticky with `top:-1px`, so subtract 1 to align visually.
    const top = Math.max(0, Math.round(anchor.offsetHeight - 1));
    document.body.style.setProperty('--pdp-sticky-bar-top', `${top}px`);
  }

  /** Keep header / buy-bar height vars in sync for gallery offset + intersection observer. */
  function syncPdpHeaderStackMetrics() {
    const header = document.getElementById('header-component');
    const headerSection = header?.closest('.header-section');
    const bar = document.querySelector(BAR_SELECTOR);

    const navHeight = getNavHeight();
    if (navHeight > 0) {
      document.body.style.setProperty('--pdp-nav-height', `${navHeight}px`);
    }

    const themeHeaderHeight = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--header-height')
    );
    const stackBase =
      Number.isFinite(themeHeaderHeight) && themeHeaderHeight > 0 ? themeHeaderHeight : navHeight;

    let stackHeight = stackBase;
    let buyBarHeight = 0;
    if (bar instanceof HTMLElement && bar.classList.contains('is-visible')) {
      buyBarHeight = bar.offsetHeight;
      if (buyBarHeight > 0) stackHeight += buyBarHeight;
    }
    document.body.style.setProperty('--pdp-buy-bar-height', `${buyBarHeight}px`);

    if (stackHeight > 0) {
      document.body.style.setProperty('--pdp-header-stack-height', `${stackHeight}px`);
    } else if (headerSection instanceof HTMLElement && headerSection.offsetHeight > 0) {
      document.body.style.setProperty(
        '--pdp-header-stack-height',
        `${headerSection.offsetHeight}px`
      );
    }
  }

  /**
   * Show when main ATC is off-screen; hide while it is visible in the viewport.
   * @param {{ isIntersecting: boolean, boundingClientRect: DOMRectReadOnly }} entry
   * @returns {boolean}
   */
  function shouldShowStickyBar(entry) {
    // Never show on initial top-of-page render.
    // Prevents the bar covering PDP header eyebrow / badges if the observer
    // target isn't found/visible yet.
    const scrollY = window.scrollY || window.pageYOffset || 0;
    if (scrollY < 24) return false;

    if (entry.isIntersecting) return false;

    const headerOffset = getHeaderOffset();
    const rect = entry.boundingClientRect;
    const scrolledPast = rect.bottom < headerOffset;
    const belowFold = rect.top >= window.innerHeight;

    // Mobile: if the main CTA is below the fold on initial load,
    // don't show the sticky bar until the shopper actually scrolls.
    const hasScrolled = scrollY > 24;
    const allowBelowFold = hasScrolled;

    return scrolledPast || (belowFold && allowBelowFold);
  }

  /**
   * @param {HTMLElement} bar
   * @param {number} productId
   * @param {boolean} isBcc
   */
  function initBarVisibility(bar, productId, isBcc) {
    if (bar.dataset.pdpStickyRevealInit === '1') return;
    bar.dataset.pdpStickyRevealInit = '1';

    let observer = null;
    let observedTarget = null;
    let waitObserver = null;

    function updateFromEntry(entry) {
      setBarVisible(bar, shouldShowStickyBar(entry));
    }

    function syncFromTarget(target) {
      if (!(target instanceof HTMLElement)) return;
      const rect = target.getBoundingClientRect();
      const headerOffset = getHeaderOffset();
      const intersects =
        rect.top < window.innerHeight &&
        rect.bottom > headerOffset &&
        rect.left < window.innerWidth &&
        rect.right > 0;

      updateFromEntry({
        isIntersecting: intersects,
        boundingClientRect: rect,
      });
    }

    function attach(target) {
      if (!(target instanceof HTMLElement) || target === observedTarget) return;
      observer?.disconnect();
      observedTarget = target;

      function createObserver() {
        observer?.disconnect();
        observer = new IntersectionObserver(
          function (entries) {
            if (entries[0]) updateFromEntry(entries[0]);
          },
          {
            threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
            rootMargin: `-${getHeaderOffset()}px 0px 0px 0px`,
          }
        );
        observer.observe(target);
      }

      createObserver();
      requestAnimationFrame(function () {
        syncFromTarget(target);
      });

      // Recreate observer when header offset changes (announcement rows, resize).
      if (window.ResizeObserver) {
        const header = document.getElementById('header-component');
        const headerSection = header?.closest?.('.header-section');
        const anchor =
          headerSection instanceof HTMLElement
            ? headerSection
            : document.getElementById('header-group');
        if (anchor instanceof HTMLElement) {
          const ro = new ResizeObserver(() => {
            syncStickyBarTopOffset();
            createObserver();
            syncFromTarget(target);
          });
          ro.observe(anchor);
        }
      }
    }

    function resolveTarget() {
      return findMainPurchaseCta(productId, isBcc);
    }

    function boot() {
      const target = resolveTarget();
      if (target) {
        attach(target);
        return;
      }

      // Don't show until we can resolve the main CTA.
      setBarVisible(bar, false);

      if (!waitObserver && window.MutationObserver) {
        const productInfo = document.querySelector('.product-information');
        if (productInfo) {
          waitObserver = new MutationObserver(function () {
            const resolved = resolveTarget();
            if (resolved) {
              attach(resolved);
              waitObserver?.disconnect();
              waitObserver = null;
            }
          });
          waitObserver.observe(productInfo, { childList: true, subtree: true });
        }
      }
    }

    boot();

    window.addEventListener(
      'resize',
      function () {
        if (observedTarget && observer) {
          observer.disconnect();
          observer.observe(observedTarget);
          syncFromTarget(observedTarget);
        }
        syncPdpHeaderStackMetrics();
        syncStickyBarTopOffset();
      },
      { passive: true }
    );
  }

  /** @param {HTMLElement} bar */
  function initBar(bar) {
    if (bar.dataset.pdpStickyInit === '1') return;
    bar.dataset.pdpStickyInit = '1';

    const configEl = bar.parentElement?.querySelector(CONFIG_SELECTOR) || document.querySelector(CONFIG_SELECTOR);
    if (!configEl) return;

    /** @type {{ productId: number, isBcc: boolean, moneyFormat: string, variant: any }} */
    let config;
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch (e) {
      return;
    }

    const titleEl = bar.querySelector('[data-pdp-sticky-title]');
    const priceEl = bar.querySelector('[data-pdp-sticky-price]');
    const compareEl = bar.querySelector('[data-pdp-sticky-compare]');
    const ctaEl = bar.querySelector('[data-pdp-sticky-cta]');
    const thumbWrap = bar.querySelector('.pdp-sticky-buy-bar__thumb');
    const thumbImg = bar.querySelector('.pdp-sticky-buy-bar__thumb-img');
    const metaEl = bar.querySelector('[data-pdp-sticky-meta]');
    const badgeEl = bar.querySelector('[data-pdp-sticky-badge]');
    const purchaseTypeEl = bar.querySelector('[data-pdp-sticky-purchase-type]');

    const isBcc = isBccPage(config.isBcc);

    initBarVisibility(bar, config.productId, isBcc);

    /**
     * Update sticky bar price, compare, per-can meta, and subscription UI for the
     * current box count (Fridge Fill ladder / quantity input) and purchase option.
     */
    function applyStickyLinePricing() {
      const qty = getPackQuantity(config.productId);
      const { priceCents, compareCents, isSubscription, checked, hasSubscriptionWidget } =
        getActiveUnitPrices(config);

      const volumeDiscount = getVolumeDiscountCents(config.productId, qty, isSubscription);
      const grossPrice = priceCents * qty;
      const linePrice = Math.max(0, grossPrice - volumeDiscount);
      const lineCompare = Math.max(compareCents * qty, volumeDiscount > 0 ? grossPrice : 0);
      const hasSaving = lineCompare > linePrice;
      const pct = hasSaving ? Math.round(((lineCompare - linePrice) / lineCompare) * 100) : 0;

      if (priceEl) priceEl.textContent = formatMoney(linePrice, config.moneyFormat);

      if (compareEl) {
        if (hasSaving) {
          compareEl.textContent = formatMoney(lineCompare, config.moneyFormat);
          compareEl.hidden = false;
          compareEl.classList.remove('hidden');
        } else {
          compareEl.hidden = true;
          compareEl.classList.add('hidden');
        }
      }

      if (hasSubscriptionWidget && checked instanceof HTMLInputElement) {
        if (badgeEl) {
          if (pct > 0) {
            badgeEl.textContent = pct + '% Off';
            badgeEl.hidden = false;
            badgeEl.classList.remove('hidden');
          } else {
            badgeEl.hidden = true;
            badgeEl.classList.add('hidden');
          }
        }

        if (purchaseTypeEl) {
          let label = 'One time';
          if (isSubscription) {
            const aria = checked.getAttribute('aria-label') || '';
            const main = aria.split(/\.\s*Product price/i)[0].trim();
            const freq = main.split(',')[0].replace(/^deliver\s*/i, '').trim();
            label = freq ? 'Subscribe · ' + freq : 'Subscribe & save';
          }
          purchaseTypeEl.textContent = label;
          purchaseTypeEl.hidden = false;
        }

        if (metaEl) metaEl.hidden = true;
      } else {
        if (badgeEl) {
          badgeEl.hidden = true;
          badgeEl.classList.add('hidden');
        }
        if (purchaseTypeEl) purchaseTypeEl.hidden = true;
        updatePackMeta(
          metaEl,
          linePrice,
          qty,
          config.packMeta,
          config.moneyFormat,
          bar.classList.contains('is-limited-edition')
        );
      }
    }

    /** @param {any} variant */
    function applyVariant(variant) {
      if (!variant) return;
      config.variant = { ...config.variant, ...variant };

      if (titleEl && variant.title) titleEl.textContent = variant.title;

      if (ctaEl) ctaEl.disabled = variant.available === false;

      // Keep pack-shot clearcuts (cans/glass) — don't swap back to packaged hero art.
      const keepClearcut =
        thumbImg?.hasAttribute('data-pdp-sticky-clearcut') ||
        thumbWrap?.hasAttribute('data-pdp-sticky-clearcut-thumb');

      if (!keepClearcut) {
        const image =
          variant.featured_media?.preview_image?.src ||
          variant.featured_image?.src ||
          variant.image ||
          config.variant.image;

        if (thumbImg && image) {
          thumbImg.src = image;
          thumbImg.removeAttribute('hidden');
        } else if (thumbWrap && !image) {
          thumbWrap.setAttribute('hidden', '');
        }
      }

      applyStickyLinePricing();
    }

    /**
     * Reflect the selected Shopify Subscriptions option (one-time vs subscribe)
     * in the sticky bar: price, strikethrough compare, % discount badge and a
     * purchase-type line. No-op when the product has no subscription widget.
     */
    function initSubscriptionSync() {
      const getFieldset = () => document.querySelector('.shopify_subscriptions_fieldset');

      function applySelection() {
        const fieldset = getFieldset();
        if (!fieldset) return false;

        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return false;

        applyStickyLinePricing();
        return true;
      }

      function bindFieldset(fieldset) {
        if (!(fieldset instanceof HTMLElement) || fieldset.dataset.pdpStickySubBound === '1') return;
        fieldset.dataset.pdpStickySubBound = '1';
        fieldset.addEventListener('change', () => requestAnimationFrame(applySelection));
        fieldset.addEventListener('click', () => requestAnimationFrame(applySelection));
      }

      // Bind now if present, and keep watching in case the app block renders
      // late or re-renders on variant change.
      const existing = getFieldset();
      if (existing) {
        bindFieldset(existing);
        applySelection();
      }

      if (window.MutationObserver) {
        const host = document.querySelector('.product-information') || document.body;
        const watcher = new MutationObserver(() => {
          const fieldset = getFieldset();
          if (fieldset) {
            bindFieldset(fieldset);
            applySelection();
          }
        });
        watcher.observe(host, { childList: true, subtree: true });
      }

      return applySelection;
    }

    /** @param {CustomEvent} event */
    function onVariantUpdate(event) {
      const detail = event.detail;
      if (!detail?.resource) return;
      if (detail.data?.productId && String(detail.data.productId) !== String(config.productId)) return;

      const resource = detail.resource;
      const title =
        resource.title && resource.title !== 'Default Title'
          ? resource.title
          : resource.public_title || resource.name || config.variant.title;

      applyVariant({
        id: resource.id,
        available: resource.available,
        title: title,
        price: resource.price,
        compare_at_price: resource.compare_at_price,
        featured_media: resource.featured_media,
        featured_image: resource.featured_image,
      });
    }

    function syncFromBccPicker() {
      const picker = document.querySelector('[data-bcc-tier-picker]');
      if (!picker) return;
      const total = picker.querySelector('[data-bcc-cta-total]');

      if (total && priceEl) {
        priceEl.textContent = total.textContent.trim();
      }

      if (compareEl) {
        compareEl.hidden = true;
        compareEl.classList.add('hidden');
      }

      updatePackMeta(
        metaEl,
        config.variant.price || 0,
        1,
        config.packMeta,
        config.moneyFormat,
        bar.classList.contains('is-limited-edition')
      );
    }

    function observeBccPicker() {
      const picker = document.querySelector('[data-bcc-tier-picker]');
      if (!picker || !window.MutationObserver) return;
      const observer = new MutationObserver(syncFromBccPicker);
      observer.observe(picker, { subtree: true, childList: true, characterData: true });
      picker.addEventListener('click', function (e) {
        if (e.target.closest('[data-bcc-tier]')) {
          requestAnimationFrame(syncFromBccPicker);
        }
      });
      syncFromBccPicker();
    }

    function observeFridgeFill() {
      const ff = document.querySelector('[data-pdp-ff]');
      const qtyInput = findQuantityInput(config.productId);
      if (!ff && !qtyInput) return;

      function sync() {
        requestAnimationFrame(applyStickyLinePricing);
      }

      if (ff) {
        ff.addEventListener('click', sync);
      }

      document.addEventListener('pdp-ff:change', function (e) {
        const detail = e.detail;
        if (detail?.productId && String(detail.productId) !== String(config.productId)) return;
        sync();
      });

      if (qtyInput) {
        qtyInput.addEventListener('input', sync);
        qtyInput.addEventListener('change', sync);
      }

      sync();
    }

    function triggerAddToCart() {
      const mainCta = findMainPurchaseCta(config.productId, isBcc);
      if (mainCta && !mainCta.disabled) {
        mainCta.click();
        return;
      }

      if (isBcc) return;

      const form = findAddToCartForm(config.productId);

      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      }
    }

    if (ctaEl) {
      ctaEl.addEventListener('click', triggerAddToCart);
    }

    document.addEventListener(VARIANT_EVENT, onVariantUpdate);

    applyVariant(config.variant);

    if (isBcc) {
      observeBccPicker();
      syncFromBccPicker();
    } else {
      // Subscription products: keep the bar in sync with the selected option.
      config.__syncSubscription = initSubscriptionSync();
      observeFridgeFill();
    }
  }

  function init() {
    syncStickyBarTopOffset();
    syncPdpHeaderStackMetrics();
    document.querySelectorAll(BAR_SELECTOR).forEach(initBar);

    const header = document.getElementById('header-component');
    const headerSection = header?.closest?.('.header-section');
    const bar = document.querySelector(BAR_SELECTOR);

    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => {
        syncPdpHeaderStackMetrics();
        syncStickyBarTopOffset();
      });
      if (header instanceof HTMLElement) ro.observe(header);
      if (headerSection instanceof HTMLElement) ro.observe(headerSection);
      if (bar instanceof HTMLElement) ro.observe(bar);
    }

    window.addEventListener('resize', syncPdpHeaderStackMetrics, { passive: true });
    window.addEventListener('resize', syncStickyBarTopOffset, { passive: true });

    document.addEventListener('dialog:open', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-component')) {
        return;
      }
      document.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
        if (bar instanceof HTMLElement) setBarVisible(bar, false);
      });
      syncPdpHeaderStackMetrics();
    });

    document.addEventListener('dialog:close', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-component')) {
        return;
      }
      requestAnimationFrame(() => {
        syncPdpHeaderStackMetrics();
        syncStickyBarTopOffset();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
